import React, { useState } from "react";
import { useAppSelector } from "../../app/hooks.ts";
import { 
  useGetCategoryTypesByCompanyQuery, 
  useGetCategoriesByCategoryTypeQuery,
} from "../../services/category.api.ts";
import { selectAuth } from "../../features/authSlice.ts";
import { useGetAllTicketTypesQuery } from "../../services/ticket_type.api.ts";
import { useGetClientsByCompanyQuery } from "../../services/client.api.ts";
import { useCreateTicketMutation } from "../../services/ticket.api.ts";
import { toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';
import './MiniCreateTicket.css'

const MiniCreateTicket = () => {
  const { agentData } = useAppSelector(selectAuth);
  const navigate = useNavigate();
  const [createTicket, { isLoading: isCreatingTicket }] = useCreateTicketMutation();

  // Form state
  const [ticketTitle, setTicketTitle] = useState("");
  const [ticketDescription, setTicketDescription] = useState("");
  const [ticketTypeId, setTicketTypeId] = useState<number | undefined>(undefined);
  const [clientId, setClientId] = useState<number | undefined>(undefined);
  const [selectedCategoryTypeId, setSelectedCategoryTypeId] = useState<number | undefined>(undefined);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);

  // Queries
  const { data: ticketTypes } = useGetAllTicketTypesQuery();
  const { data: clients } = useGetClientsByCompanyQuery(agentData!.belongs_to_company_id);
  const { data: categoryTypes } = useGetCategoryTypesByCompanyQuery(agentData!.belongs_to_company_id);
  const { data: categories } = useGetCategoriesByCategoryTypeQuery(
    selectedCategoryTypeId!, 
    { skip: !selectedCategoryTypeId }
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!ticketTitle || !ticketDescription || !ticketTypeId || !selectedCategoryTypeId || !selectedCategoryId || !clientId) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const ticketData = {
        ticket_title: ticketTitle,
        ticket_description: ticketDescription,
        ticket_type_id: ticketTypeId,
        category_type_id: selectedCategoryTypeId,
        category_id: selectedCategoryId,
        client_id: clientId,
        company_id: agentData!.belongs_to_company_id
      };

      const { ticket } = await createTicket(ticketData).unwrap();
      toast.success("Ticket created successfully");
      navigate(`/ticket/${ticket.ticket_id}`);
    } catch (error) {
      console.error("Failed to create ticket", error);
      toast.error("Failed to create ticket. Please try again.");
    }
  };

  const handleCategoryTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value ? parseInt(event.target.value) : undefined;
    setSelectedCategoryTypeId(value);
    setSelectedCategoryId(undefined);
  };

  return (
    <div className="mini-create-ticket">
      <h3 className="mini-form-title">Ticket Creation Shortcut</h3>
      <form onSubmit={handleSubmit} className="mini-ticket-form">
        <input 
          type="text" 
          placeholder="Ticket Title"
          value={ticketTitle}
          onChange={(e) => setTicketTitle(e.target.value)}
          className="mini-input"
          required 
        />

        <textarea 
          placeholder="Ticket Description"
          value={ticketDescription}
          onChange={(e) => setTicketDescription(e.target.value)}
          className="mini-textarea"
          required
        />

        <select 
          value={ticketTypeId?.toString() || ''}
          onChange={(e) => setTicketTypeId(e.target.value ? parseInt(e.target.value) : undefined)}
          className="mini-select"
          required
        >
          <option value="">Select Type</option>
          {ticketTypes?.map(type => (
            <option key={type.ticket_type_id} value={type.ticket_type_id}>
              {type.ticket_type_name}
            </option>
          ))}
        </select>

        <select 
          value={selectedCategoryTypeId?.toString() || ''}
          onChange={handleCategoryTypeChange}
          className="mini-select"
          required
        >
          <option value="">Select Category Type</option>
          {categoryTypes?.map(type => (
            <option key={type.category_type_id} value={type.category_type_id}>
              {type.category_type_name}
            </option>
          ))}
        </select>

        <select 
          value={selectedCategoryId?.toString() || ''}
          onChange={(e) => setSelectedCategoryId(e.target.value ? parseInt(e.target.value) : undefined)}
          className="mini-select"
          disabled={!selectedCategoryTypeId}
          required
        >
          <option value="">Select Category</option>
          {categories?.map(cat => (
            <option key={cat.category_id} value={cat.category_id}>
              {cat.category_name}
            </option>
          ))}
        </select>

        <select 
          value={clientId?.toString() || ''}
          onChange={(e) => setClientId(e.target.value ? parseInt(e.target.value) : undefined)}
          className="mini-select"
          required
        >
          <option value="">Select Client</option>
          {clients?.map(client => (
            <option key={client.client_id} value={client.client_id}>
              {client.client_username}
            </option>
          ))}
        </select>

        <button 
          type="submit" 
          className="mini-submit-btn"
          disabled={isCreatingTicket}
        >
          {isCreatingTicket ? "Creating..." : "Create Ticket"}
        </button>
      </form>
    </div>
  );
};

export default MiniCreateTicket;